import React, { FC, useRef } from 'react'
import { Button } from '@components/gassan-ui/Button'
import { Form, Formik } from 'formik'
import { Field, Heading } from '@components/gassan-ui'
import { emarsysNewsletterSchema } from './schema'
import { useTranslation } from 'next-i18next'
import { useNotifications } from '@components/NotificationsProvider'
import { useLocalization } from '@components/Localization'
import { useRouter } from 'next/router'
import LocalizedLink from '@components/Localization/LocalizedLink'

const EMARSYS_FORM_ACTION = 'https://link.online.gassan.com/u/register.php'

type FormValues = {
  inp_46: string
  inp_1: string
  inp_2: string
  inp_3: string
  optin: boolean
}

export const EmarsysNewsletterForm: FC = () => {
  const { t } = useTranslation('forms')
  const { parseGraphQlError } = useLocalization()
  const formRef = useRef<HTMLFormElement>(null)
  const notifications = useNotifications()
  const router = useRouter()

  // Check if current language uses the new form version
  const isFormEnglish = router.locale === 'en' || router.locale === 'de'
  const formId = isFormEnglish ? '2039' : '2034'

  const initialValues: FormValues = {
    inp_46: '',
    inp_1: '',
    inp_2: '',
    inp_3: '',
    optin: false,
  }

  const privacyLabel = (
    <>
      {t('labels.acceptNewsletterAndConditions')}
      <br />
      <LocalizedLink href={t('privacyPolicy', { ns: 'navigation' })}>
        <a href={t('privacyPolicy', { ns: 'navigation' })}>{t('labels.moreInformation')}</a>
      </LocalizedLink>
    </>
  )

  return (
    <div style={{ fontFamily: 'sans-serif' }}>
      <Heading variant="h2" style={{ textAlign: 'center' }}>
        {t('labels.moreInformation')}
      </Heading>
      <Formik
        validationSchema={emarsysNewsletterSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form ref={formRef} className="mx-auto max-w-md">
            {/* Fields required by Emarsys but not used in the form */}
            <input type="hidden" name="CID" value="1000945900" />
            <input type="hidden" name="SID" value="" />
            <input type="hidden" name="UID" value="" />
            <input type="hidden" name="f" value={formId} />
            <input type="hidden" name="p" value="2" />
            <input type="hidden" name="a" value="r" />
            <input type="hidden" name="el" value="" />
            <input type="hidden" name="llid" value="" />
            <input type="hidden" name="c" value="" />
            <input type="hidden" name="counted" value="" />
            <input type="hidden" name="RID" value="" />
            <input type="hidden" name="mailnow" value="" />

            <Field field="select" name="inp_46" label={t('labels.salutation')}>
              <option value="">{t('labels.salutation')}</option>
              {isFormEnglish && <option value="6">{t('labels.mx')}</option>}
              <option value="1">{t('labels.mr')}</option>
              <option value="2">{isFormEnglish ? t('labels.ms') : t('labels.mrs')}</option>
            </Field>

            <Field field="input" name="inp_1" label={t('labels.firstName')} maxLength={60} />
            <Field field="input" name="inp_2" label={t('labels.lastName')} maxLength={60} />
            <Field field="input" name="inp_3" label={t('labels.email')} maxLength={255} />

            <Field field="checkbox" name="optin" label={privacyLabel} />

            <Button type="submit" variant="dark" status={isSubmitting ? 'loading' : 'idle'}>
              {t('submits.sendMessage')}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  )

  async function handleSubmit(_values: FormValues, { resetForm }: any) {
    if (formRef.current) {
      try {
        const formData = new FormData(formRef.current)

        // Ensure checkbox sends "y" value when checked, consistent with Emarsys expectations
        if (_values.optin) {
          formData.set('optin', 'y')
        }

        const params = new URLSearchParams(formData as any)

        await fetch(`${EMARSYS_FORM_ACTION}?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors',
        })
        notifications.addNotification({
          variant: 'simple-confirmation',
          label: t('signupThanks', { ns: 'other' }),
        })
      } catch (error) {
        notifications.addNotification({
          variant: 'error',
          label: parseGraphQlError(error.message),
        })
      } finally {
        resetForm()
      }
    }
  }
}

export default EmarsysNewsletterForm
